/**
 * ProjectionChart.tsx – the stacked-bar chart behind every pane of the Yearly Projections modal's
 * chart accordion (cashflow, net worth, debt).
 *
 * One chart, configured three ways: a stack of series per projected year, optionally a dotted
 * reference level held across the chart (cashflow's total expenses, net worth's after-debt figure)
 * and — where a `gap` is configured and the bars fall short of that line — a solid red block
 * bridging them. Zoom/pan along the year axis, a hover/focus tooltip breaking the year down, and a
 * legend whose entries switch series off (the y-axis then animates to its new scale).
 *
 * Hand-rolled SVG rather than a charting dependency: the app ships no chart library, and the
 * behaviour here (stack + reference line + gap fill + animated rescale) is a short amount of
 * geometry. Everything is inspectable DOM, which is also what lets agedCare.ui.flow.test.ts assert
 * on the marks rather than on a screenshot.
 */
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ZoomIn, ZoomOut, RotateCcw } from 'lucide-react';
import { stackTotal, type ChartPoint, type ChartSeries } from './chartTypes.ts';

/** Chart chrome — recessive by design, so the bars are the only loud thing. */
const SURFACE = '#ffffff';
const INK_PRIMARY = '#0b0b0b';
const INK_MUTED = '#898781';
const GRIDLINE = '#e1e0d9';
const BASELINE = '#c3c2b7';

const MARGIN = { top: 16, right: 16, bottom: 40, left: 72 };
/**
 * Plot height, chosen from the viewport so a short window doesn't leave the projections table with
 * no room below the chart. Both bounds are deliberate: below the floor the bars stop being readable,
 * above the ceiling a tall window just gives the chart more height than it has data to fill.
 */
const PLOT_HEIGHT_MAX = 260;
const PLOT_HEIGHT_MIN = 150;
const PLOT_HEIGHT_VIEWPORT_FRACTION = 0.3;
/** Bars are capped rather than filling their band — the leftover band is deliberate air. */
const MAX_BAR_WIDTH = 24;
const MAX_ZOOM = 8;
/** Tooltip width, narrowed to the plot when the chart itself is smaller than this. */
const MAX_TOOLTIP_WIDTH = 288;
/**
 * One row of the legend (its content plus padding), which the empty state stands in for so a chart
 * with nothing to draw still fills the height its siblings would — switching accordion panes should
 * not make the modal jump. Approximate by nature: a legend with enough series wraps to two rows.
 */
const LEGEND_ROW_HEIGHT = 41;
/** The plot wrapper's own bottom padding, which the empty state matches for the same reason. */
const PLOT_PADDING_BOTTOM = 4;

/** The dotted level held across each band, and what to make of the space between it and the stack. */
export interface ChartReference {
    /** Test hook: the dotted line is `<chartId>-<id>-line`. */
    id: string;
    /** Legend text for the dotted key, e.g. "Total expenses". */
    label: string;
    /**
     * Fills the space between the top of the stack and the reference, where the stack falls short.
     * Charts that leave this out draw the line alone and let the distance to it be read off the
     * axis — net worth's bars are gross assets, and the drop to the line is the debt, which has a
     * pane of its own rather than a block sitting over the assets it was already netted from.
     */
    gap?: {
        id: string;
        /** Legend/summary text while the stack falls SHORT of the reference, e.g. "Shortfall". */
        label: string;
        /** Summary text while the stack CLEARS it, e.g. "Surplus". */
        clearedLabel: string;
        color: string;
    };
}

export interface ProjectionChartProps {
    /** Stable id, prefixing every `data-testid` this chart emits. */
    chartId: string;
    /** Chart name as it reads inside a control label — "Zoom in to {name} chart". */
    name: string;
    title: string;
    subtitle: string;
    /** Sentence appended to the plot's accessible name, describing what the marks mean. */
    ariaDescription: string;
    points: ChartPoint[];
    series: readonly ChartSeries[];
    /** Heading for the stack breakdown in the tooltip, e.g. "Income". */
    stackLabel: string;
    /** Ruled-off total under that breakdown, e.g. "Total income". */
    stackTotalLabel: string;
    yAxisLabel: string;
    xAxisLabel: string;
    reference?: ChartReference;
    /** Full currency formatter (the modal's, so the today's-dollars toggle applies). */
    formatValue: (value: number) => string;
    /** Shown in place of the plot when there is nothing to draw — e.g. a client carrying no debt. */
    emptyMessage: string;
}

/**
 * A dollars axis whose ticks land on clean numbers: a step of 1 / 2 / 2.5 / 5 × a power of ten,
 * with the top a whole number of those steps above zero.
 */
function niceScale(peak: number, tickCount = 4): { max: number; ticks: number[] } {
    if (!Number.isFinite(peak) || peak <= 0) return { max: 1000, ticks: [0, 250, 500, 750, 1000] };
    const magnitude = Math.pow(10, Math.floor(Math.log10(peak / tickCount)));
    const normalised = peak / tickCount / magnitude;
    const step = (normalised <= 1 ? 1 : normalised <= 2 ? 2 : normalised <= 2.5 ? 2.5 : normalised <= 5 ? 5 : 10) * magnitude;
    const max = step * Math.ceil(peak / step);
    const ticks: number[] = [];
    for (let value = 0; value <= max + step / 2; value += step) ticks.push(value);
    return { max, ticks };
}

/** Compact axis tick text — the tooltip and the table below carry the exact figures. */
function formatTick(value: number): string {
    const abs = Math.abs(value);
    const compact = (divisor: number, suffix: string) => {
        const scaled = value / divisor;
        return `$${Number.isInteger(scaled) ? scaled : scaled.toFixed(1)}${suffix}`;
    };
    if (abs >= 1_000_000) return compact(1_000_000, 'm');
    if (abs >= 1_000) return compact(1_000, 'k');
    return `$${Math.round(value)}`;
}

/** A bar segment: square where it meets the segment below, 4px rounded where it caps the stack. */
function segmentPath(x: number, y: number, width: number, height: number, roundTop: boolean): string {
    const r = roundTop ? Math.max(0, Math.min(4, width / 2, height)) : 0;
    if (r === 0) return `M${x},${y} h${width} v${height} h${-width} Z`;
    return `M${x},${y + height} L${x},${y + r} Q${x},${y} ${x + r},${y} L${x + width - r},${y} Q${x + width},${y} ${x + width},${y + r} L${x + width},${y + height} Z`;
}

function clamp(value: number, min: number, max: number): number {
    return Math.min(max, Math.max(min, value));
}

const prefersReducedMotion = () =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Ease a map of numbers toward `targets`, one rAF loop for the whole map. Drives both the per-series
 * on/off weights and the y-axis top, so a legend toggle shrinks the hidden segment and rescales the
 * axis as a single coherent movement. Honours `prefers-reduced-motion` by jumping straight there.
 */
function useTween(targets: Record<string, number>, duration = 450): Record<string, number> {
    const [values, setValues] = useState(targets);
    const currentRef = useRef(targets);
    const frameRef = useRef(0);
    // Value-based dependency: `targets` is rebuilt every render, so using its identity would restart
    // the tween on every frame it triggers.
    const targetKey = Object.keys(targets)
        .sort()
        .map((key) => `${key}:${targets[key]}`)
        .join('|');

    useEffect(() => {
        if (prefersReducedMotion()) {
            cancelAnimationFrame(frameRef.current);
            currentRef.current = targets;
            setValues(targets);
            return;
        }
        const from = { ...currentRef.current };
        const startedAt = performance.now();
        const step = (now: number) => {
            const t = clamp((now - startedAt) / duration, 0, 1);
            const eased = 1 - Math.pow(1 - t, 3);
            const next: Record<string, number> = {};
            Object.keys(targets).forEach((key) => {
                const origin = from[key] ?? targets[key];
                next[key] = origin + (targets[key] - origin) * eased;
            });
            currentRef.current = next;
            setValues(next);
            if (t < 1) frameRef.current = requestAnimationFrame(step);
        };
        cancelAnimationFrame(frameRef.current);
        frameRef.current = requestAnimationFrame(step);
        return () => cancelAnimationFrame(frameRef.current);
        // `targets` is deliberately excluded — `targetKey` is its value-equality stand-in.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [targetKey, duration]);

    return values;
}

const ProjectionChart = ({
    chartId,
    name,
    title,
    subtitle,
    ariaDescription,
    points,
    series: configuredSeries,
    stackLabel,
    stackTotalLabel,
    yAxisLabel,
    xAxisLabel,
    reference,
    formatValue,
    emptyMessage,
}: ProjectionChartProps) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const svgRef = useRef<SVGSVGElement>(null);
    const [width, setWidth] = useState(0);
    const [viewportHeight, setViewportHeight] = useState(() => (typeof window === 'undefined' ? 900 : window.innerHeight));
    const [hiddenSeries, setHiddenSeries] = useState<Record<string, boolean>>({});
    const [zoom, setZoom] = useState(1);
    const [pan, setPan] = useState(0);
    const [dragging, setDragging] = useState(false);
    const [hover, setHover] = useState<{ index: number; x: number; y: number } | null>(null);
    const dragRef = useRef<{ pointerId: number; startX: number; startPan: number } | null>(null);

    const isEnabled = useCallback((id: string) => !hiddenSeries[id], [hiddenSeries]);

    // A series that is zero in every year is a category this client simply doesn't have (no salary,
    // no credit cards). Dropping it keeps the legend to what's actually on screen — the configured
    // list is the full vocabulary, not a promise to show every word of it.
    const series = useMemo(() => configuredSeries.filter((entry) => points.some((point) => (point.values[entry.id] || 0) > 0)), [configuredSeries, points]);

    // --- Scales -------------------------------------------------------------------------------
    const innerWidth = Math.max(0, width - MARGIN.left - MARGIN.right);
    const contentWidth = innerWidth * zoom;
    const bandWidth = points.length > 0 ? contentWidth / points.length : 0;
    const barWidth = Math.min(MAX_BAR_WIDTH, bandWidth * 0.55);
    const plotHeight = clamp(Math.round(viewportHeight * PLOT_HEIGHT_VIEWPORT_FRACTION), PLOT_HEIGHT_MIN, PLOT_HEIGHT_MAX);
    const plotBottom = MARGIN.top + plotHeight;
    const pannable = contentWidth > innerWidth + 0.5;
    const minPan = Math.min(0, innerWidth - contentWidth);

    // The axis top spans EVERY year, not just the visible ones, so panning never rescales y — only a
    // legend toggle does, which is what makes that rescale legible as an animation.
    // Only a reference the chart actually DRAWS gets a say in the axis top: a point may carry one
    // for its tooltip while the chart is configured without a line (net worth's figure sits under
    // its own bars, so making room for it would only add air).
    const scale = useMemo(
        () => niceScale(points.reduce((max, point) => Math.max(max, stackTotal(point, series, isEnabled), reference ? (point.reference ?? 0) : 0), 0)),
        [points, series, isEnabled, reference],
    );
    const targetYMax = scale.max;

    const tweenTargets = useMemo(() => {
        const targets: Record<string, number> = { yMax: targetYMax };
        series.forEach((entry) => {
            targets[entry.id] = isEnabled(entry.id) ? 1 : 0;
        });
        return targets;
    }, [targetYMax, series, isEnabled]);
    const tweened = useTween(tweenTargets);
    const yMax = Math.max(1, tweened.yMax ?? targetYMax);
    const weightOf = (id: string) => clamp(tweened[id] ?? 1, 0, 1);

    const yFor = (value: number) => plotBottom - (value / yMax) * plotHeight;
    const bandLeft = (index: number) => MARGIN.left + pan + index * bandWidth;
    const isBandVisible = (index: number) => bandLeft(index) + bandWidth >= MARGIN.left && bandLeft(index) <= MARGIN.left + innerWidth;

    // Tick VALUES come from the settled scale so the labels stay clean; their POSITIONS use the
    // tweened axis top, so the gridlines slide into place with the bars.
    const ticks = scale.ticks;

    // --- Sizing -------------------------------------------------------------------------------
    // Width tracks the container (which the modal sizes), height tracks the viewport. Between them
    // the chart re-lays-out on any window resize rather than being drawn once at its opening size.
    useLayoutEffect(() => {
        const element = containerRef.current;
        if (!element) return;
        setWidth(element.clientWidth);
        if (typeof ResizeObserver === 'undefined') return;
        const observer = new ResizeObserver((entries) => setWidth(entries[0].contentRect.width));
        observer.observe(element);
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        const onResize = () => setViewportHeight(window.innerHeight);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    // Re-clamp the pan whenever zoom or available width changes, so bars can never be left stranded
    // outside the plot area.
    useEffect(() => {
        setPan((current) => clamp(current, minPan, 0));
    }, [minPan]);

    // --- Zoom & pan ---------------------------------------------------------------------------
    /** Zoom about a point (in inner-plot px), keeping the year under that point fixed. */
    const zoomAbout = useCallback(
        (factor: number, anchorX: number) => {
            const nextZoom = clamp(zoom * factor, 1, MAX_ZOOM);
            if (nextZoom === zoom) return;
            const nextContent = innerWidth * nextZoom;
            const dataFraction = contentWidth > 0 ? (anchorX - pan) / contentWidth : 0;
            setZoom(nextZoom);
            setPan(clamp(anchorX - dataFraction * nextContent, Math.min(0, innerWidth - nextContent), 0));
        },
        [zoom, pan, innerWidth, contentWidth],
    );

    // Attached natively: React registers onWheel passively, so it cannot preventDefault — and without
    // that the modal scrolls behind the chart while the reader is zooming it.
    useEffect(() => {
        const svg = svgRef.current;
        if (!svg) return;
        const onWheel = (event: WheelEvent) => {
            if (innerWidth <= 0) return;
            event.preventDefault();
            const rect = svg.getBoundingClientRect();
            zoomAbout(event.deltaY < 0 ? 1.15 : 1 / 1.15, event.clientX - rect.left - MARGIN.left);
        };
        svg.addEventListener('wheel', onWheel, { passive: false });
        return () => svg.removeEventListener('wheel', onWheel);
    }, [zoomAbout, innerWidth]);

    const handlePointerDown = (event: React.PointerEvent<SVGSVGElement>) => {
        if (event.button !== 0 || !pannable) return;
        dragRef.current = { pointerId: event.pointerId, startX: event.clientX, startPan: pan };
        setDragging(true);
        setHover(null);
        event.currentTarget.setPointerCapture(event.pointerId);
    };

    const handlePointerMove = (event: React.PointerEvent<SVGSVGElement>) => {
        const drag = dragRef.current;
        if (drag && drag.pointerId === event.pointerId) {
            setPan(clamp(drag.startPan + (event.clientX - drag.startX), minPan, 0));
            return;
        }
        if (bandWidth <= 0) return;
        const rect = event.currentTarget.getBoundingClientRect();
        const localX = event.clientX - rect.left;
        const index = Math.floor((localX - MARGIN.left - pan) / bandWidth);
        if (index < 0 || index >= points.length || localX < MARGIN.left || localX > MARGIN.left + innerWidth) {
            setHover(null);
            return;
        }
        setHover({ index, x: localX, y: event.clientY - rect.top });
    };

    const endDrag = (event: React.PointerEvent<SVGSVGElement>) => {
        const drag = dragRef.current;
        if (!drag || drag.pointerId !== event.pointerId) return;
        dragRef.current = null;
        setDragging(false);
        if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    };

    const resetView = () => {
        setZoom(1);
        setPan(0);
    };

    const toggleSeries = (id: string) => setHiddenSeries((current) => ({ ...current, [id]: !current[id] }));

    // --- Hover readout ------------------------------------------------------------------------
    const hoveredPoint = hover ? points[hover.index] : null;
    const hoveredTotal = hoveredPoint ? stackTotal(hoveredPoint, series, isEnabled) : 0;
    /** Only meaningful where `reference.gap` is configured: how far the stack falls short of the line. */
    const hoveredGap = hoveredPoint ? (hoveredPoint.reference ?? 0) - hoveredTotal : 0;
    // A configured gap OWNS the summary line: it moves with the legend, so it can only be derived
    // here. Charts without one fall back to whatever summary their data carries.
    const hoveredSummary = reference?.gap
        ? { label: hoveredGap > 0 ? reference.gap.label : reference.gap.clearedLabel, value: Math.abs(hoveredGap), tone: hoveredGap > 0 ? 'negative' : 'positive' }
        : hoveredPoint?.summary;

    /** One-line summary used for the band's accessible name — the keyboard equivalent of the tooltip. */
    const describePoint = (point: ChartPoint) => {
        const total = stackTotal(point, series, isEnabled);
        const parts = [`${stackTotalLabel} ${formatValue(total)}`];
        if (reference) {
            parts.push(`${reference.label} ${formatValue(point.reference ?? 0)}`);
            if (reference.gap) {
                const gap = (point.reference ?? 0) - total;
                parts.push(`${gap > 0 ? reference.gap.label : reference.gap.clearedLabel} ${formatValue(Math.abs(gap))}`);
            }
        }
        if (point.summary && !reference?.gap) parts.push(`${point.summary.label} ${formatValue(point.summary.value)}`);
        return `${point.fullLabel}: ${parts.join(', ')}`;
    };

    if (points.length === 0) return null;

    const svgHeight = MARGIN.top + plotHeight + MARGIN.bottom;
    const tooltipWidth = Math.min(MAX_TOOLTIP_WIDTH, width || MAX_TOOLTIP_WIDTH);
    const clipId = `${chartId}-chart-plot-clip`;
    const xAxisClipId = `${chartId}-chart-xaxis-clip`;
    // Nothing to draw at all (a client with no debt, say): axes and a legend would only frame an
    // empty plot, so say so in words instead — at the height they would have taken.
    const hasData = points.some((point) => series.some((entry) => (point.values[entry.id] || 0) > 0) || (reference ? (point.reference ?? 0) > 0 : false));

    return (
        <div>
            <div className="flex flex-wrap items-start justify-between gap-2 px-4 pt-3">
                <div>
                    <h5 className="text-sm font-semibold text-slate-800">{title}</h5>
                    {/* The zoom/pan hint is the component's own, and only true while there is a plot. */}
                    <p className="text-xs text-slate-500">{hasData ? `${subtitle} Scroll to zoom; drag to pan once zoomed in.` : subtitle}</p>
                </div>
                {hasData ? (
                    <div className="flex items-center gap-1">
                        <button
                            type="button"
                            onClick={() => zoomAbout(1 / 1.4, innerWidth / 2)}
                            disabled={zoom <= 1}
                            className="rounded-md p-1.5 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-800 disabled:opacity-40 disabled:hover:bg-transparent"
                            aria-label={`Zoom out of ${name} chart`}
                            title="Zoom out"
                        >
                            <ZoomOut className="h-4 w-4" aria-hidden />
                        </button>
                        <button
                            type="button"
                            onClick={() => zoomAbout(1.4, innerWidth / 2)}
                            disabled={zoom >= MAX_ZOOM}
                            className="rounded-md p-1.5 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-800 disabled:opacity-40 disabled:hover:bg-transparent"
                            aria-label={`Zoom in to ${name} chart`}
                            title="Zoom in"
                        >
                            <ZoomIn className="h-4 w-4" aria-hidden />
                        </button>
                        <button
                            type="button"
                            onClick={resetView}
                            disabled={zoom === 1 && pan === 0}
                            className="rounded-md p-1.5 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-800 disabled:opacity-40 disabled:hover:bg-transparent"
                            aria-label={`Reset ${name} chart zoom`}
                            title="Reset zoom"
                        >
                            <RotateCcw className="h-4 w-4" aria-hidden />
                        </button>
                    </div>
                ) : null}
            </div>

            {!hasData ? (
                <div className="flex items-center justify-center px-4" style={{ height: svgHeight + PLOT_PADDING_BOTTOM + LEGEND_ROW_HEIGHT }}>
                    <p className="text-center text-sm text-slate-500" data-testid={`${chartId}-empty`}>
                        {emptyMessage}
                    </p>
                </div>
            ) : (
                <>
                    <div className="px-4 pb-1">
                        {/* Measured separately from the padded wrapper so `clientWidth` is the true plot width. */}
                        <div ref={containerRef} className="relative">
                            <svg
                                ref={svgRef}
                                width={width || undefined}
                                height={svgHeight}
                                role="group"
                                aria-label={`${title}: ${ariaDescription}`}
                                className={`touch-none select-none ${pannable ? (dragging ? 'cursor-grabbing' : 'cursor-grab') : ''}`}
                                onPointerDown={handlePointerDown}
                                onPointerMove={handlePointerMove}
                                onPointerUp={endDrag}
                                onPointerCancel={endDrag}
                                onPointerLeave={(event) => {
                                    endDrag(event);
                                    setHover(null);
                                }}
                            >
                                <defs>
                                    <clipPath id={clipId}>
                                        <rect x={MARGIN.left} y={MARGIN.top - 8} width={innerWidth} height={plotHeight + 8} />
                                    </clipPath>
                                    {/* The year labels sit BELOW the plot, so they need their own band to be clipped
                                        horizontally by (they'd vanish entirely inside the plot clip). */}
                                    <clipPath id={xAxisClipId}>
                                        <rect x={MARGIN.left} y={plotBottom} width={innerWidth} height={MARGIN.bottom} />
                                    </clipPath>
                                </defs>

                                {/* Dollars axis: hairline gridlines, ticks rounded to clean numbers. */}
                                {ticks.map((tick) => (
                                    <g key={`tick-${tick}`}>
                                        <line x1={MARGIN.left} x2={MARGIN.left + innerWidth} y1={yFor(tick)} y2={yFor(tick)} stroke={tick === 0 ? BASELINE : GRIDLINE} strokeWidth={1} />
                                        <text
                                            x={MARGIN.left - 10}
                                            y={yFor(tick) + 4}
                                            textAnchor="end"
                                            fontSize={11}
                                            fill={INK_MUTED}
                                            data-testid={`${chartId}-y-tick`}
                                            style={{ fontVariantNumeric: 'tabular-nums' }}
                                        >
                                            {formatTick(tick)}
                                        </text>
                                    </g>
                                ))}
                                <text x={14} y={MARGIN.top + plotHeight / 2} fontSize={11} fill={INK_MUTED} textAnchor="middle" transform={`rotate(-90 14 ${MARGIN.top + plotHeight / 2})`}>
                                    {yAxisLabel}
                                </text>

                                <g clipPath={`url(#${clipId})`}>
                                    {points.map((point, index) => {
                                        if (!isBandVisible(index)) return null;
                                        const barX = bandLeft(index) + (bandWidth - barWidth) / 2;
                                        const drawn = series
                                            .map((entry) => ({ entry, value: (point.values[entry.id] || 0) * weightOf(entry.id) }))
                                            .filter((slot) => slot.value > 0);
                                        const total = drawn.reduce((sum, slot) => sum + slot.value, 0);
                                        const gap = reference?.gap ? Math.max(0, (point.reference ?? 0) - total) : 0;
                                        const topSlotIndex = drawn.length - 1;

                                        let cumulative = 0;
                                        return (
                                            <g key={`band-${index}`}>
                                                {drawn.map(({ entry, value }, drawnIndex) => {
                                                    const top = yFor(cumulative + value);
                                                    const bottom = yFor(cumulative);
                                                    // Segments butt together — the colour change is the divider, so the stack
                                                    // reads as one continuous column against the reference line.
                                                    const height = bottom - top;
                                                    cumulative += value;
                                                    if (height < 0.5) return null;
                                                    return (
                                                        <path
                                                            key={entry.id}
                                                            d={segmentPath(barX, top, barWidth, height, gap <= 0 && drawnIndex === topSlotIndex)}
                                                            fill={entry.color}
                                                            data-testid={`${chartId}-segment-${entry.id}`}
                                                            data-year-index={index}
                                                            style={{ pointerEvents: 'none' }}
                                                            aria-hidden
                                                        />
                                                    );
                                                })}
                                                {gap > 0 && reference?.gap ? (
                                                    <path
                                                        d={segmentPath(
                                                            barX,
                                                            yFor(point.reference ?? 0),
                                                            barWidth,
                                                            Math.max(0, yFor(total) - yFor(point.reference ?? 0)),
                                                            true,
                                                        )}
                                                        fill={reference.gap.color}
                                                        data-testid={`${chartId}-segment-${reference.gap.id}`}
                                                        data-year-index={index}
                                                        style={{ pointerEvents: 'none' }}
                                                        aria-hidden
                                                    />
                                                ) : null}
                                            </g>
                                        );
                                    })}

                                    {/* The reference level — a dotted line held across each year, stepping between them. */}
                                    {reference ? (
                                        <path
                                            d={points
                                                .map(
                                                    (point, index) =>
                                                        `${index === 0 ? 'M' : 'L'}${bandLeft(index)},${yFor(point.reference ?? 0)} L${bandLeft(index) + bandWidth},${yFor(point.reference ?? 0)}`,
                                                )
                                                .join(' ')}
                                            fill="none"
                                            stroke={INK_PRIMARY}
                                            strokeWidth={2}
                                            strokeDasharray="1 5"
                                            strokeLinecap="round"
                                            data-testid={`${chartId}-${reference.id}-line`}
                                            aria-hidden
                                        />
                                    ) : null}

                                    {/* Hover/focus targets — the whole band, so the pointer never has to find a 24px bar. */}
                                    {points.map((point, index) =>
                                        isBandVisible(index) ? (
                                            <rect
                                                key={`hit-${index}`}
                                                x={bandLeft(index)}
                                                y={MARGIN.top}
                                                width={bandWidth}
                                                height={plotHeight}
                                                fill={INK_PRIMARY}
                                                fillOpacity={hover?.index === index ? 0.04 : 0}
                                                tabIndex={0}
                                                role="img"
                                                data-testid={`${chartId}-year-${index}`}
                                                aria-label={describePoint(point)}
                                                onFocus={() => setHover({ index, x: bandLeft(index) + bandWidth / 2, y: MARGIN.top + plotHeight / 3 })}
                                                onBlur={() => setHover((current) => (current?.index === index ? null : current))}
                                                style={{ outline: 'none' }}
                                            />
                                        ) : null,
                                    )}
                                </g>

                                {/* Year axis. */}
                                <line x1={MARGIN.left} x2={MARGIN.left + innerWidth} y1={plotBottom} y2={plotBottom} stroke={BASELINE} strokeWidth={1} />
                                <g clipPath={`url(#${xAxisClipId})`}>
                                    {points.map((point, index) =>
                                        isBandVisible(index) ? (
                                            <text key={`xtick-${index}`} x={bandLeft(index) + bandWidth / 2} y={plotBottom + 16} textAnchor="middle" fontSize={11} fill={INK_MUTED}>
                                                {point.label}
                                            </text>
                                        ) : null,
                                    )}
                                </g>
                                <text x={MARGIN.left + innerWidth / 2} y={svgHeight - 4} textAnchor="middle" fontSize={11} fill={INK_MUTED}>
                                    {xAxisLabel}
                                </text>
                            </svg>

                            {hover && hoveredPoint ? (
                                <div
                                    role="tooltip"
                                    data-testid={`${chartId}-tooltip`}
                                    className="pointer-events-none absolute z-20 rounded-lg bg-gray-800 p-3 text-xs text-white shadow-lg"
                                    style={{
                                        width: tooltipWidth,
                                        left: clamp(hover.x + 18, 0, Math.max(0, width - tooltipWidth)),
                                        top: clamp(hover.y - 30, 0, Math.max(0, svgHeight - 40)),
                                    }}
                                >
                                    <h6 className="mb-2 text-sm font-bold text-blue-300">{hoveredPoint.fullLabel}</h6>
                                    <div className="mb-1 text-[10px] uppercase tracking-wide text-gray-400">{stackLabel}</div>
                                    {series
                                        .filter((entry) => (hoveredPoint.values[entry.id] || 0) > 0)
                                        .map((entry) => (
                                            <div key={entry.id} className={`flex items-center justify-between gap-2 ${isEnabled(entry.id) ? '' : 'opacity-50'}`}>
                                                <span className="flex items-center gap-1.5 text-gray-300">
                                                    <span className="inline-block h-0.5 w-3 rounded-full" style={{ backgroundColor: entry.color }} aria-hidden />
                                                    {entry.label}
                                                    {isEnabled(entry.id) ? '' : ' (hidden)'}
                                                </span>
                                                <span className={`font-mono font-semibold ${isEnabled(entry.id) ? '' : 'line-through'}`}>{formatValue(hoveredPoint.values[entry.id] || 0)}</span>
                                            </div>
                                        ))}
                                    <div className="mt-1 flex items-center justify-between gap-2 border-t border-gray-600 pt-1">
                                        <span className="text-gray-400">{stackTotalLabel}</span>
                                        <span className="font-mono font-semibold">{formatValue(hoveredTotal)}</span>
                                    </div>
                                    {(hoveredPoint.sections || []).map((section) => (
                                        <div key={section.title}>
                                            <div className="mb-1 mt-2 text-[10px] uppercase tracking-wide text-gray-400">{section.title}</div>
                                            {section.lines.map((line) => (
                                                <div key={line.label} className="flex items-center justify-between gap-2">
                                                    <span className="text-gray-300">{line.label}</span>
                                                    <span className="font-mono font-semibold">{formatValue(line.value)}</span>
                                                </div>
                                            ))}
                                            {section.total ? (
                                                <div className="mt-1 flex items-center justify-between gap-2 border-t border-gray-600 pt-1">
                                                    <span className="text-gray-400">{section.total.label}</span>
                                                    <span className="font-mono font-semibold">{formatValue(section.total.value)}</span>
                                                </div>
                                            ) : null}
                                        </div>
                                    ))}
                                    {hoveredSummary ? (
                                        <div
                                            className="mt-2 flex items-center justify-between gap-2 rounded px-2 py-1"
                                            style={{ backgroundColor: hoveredSummary.tone === 'negative' ? 'rgba(208,59,59,0.22)' : 'rgba(12,163,12,0.18)' }}
                                        >
                                            <span className="text-[10px] uppercase tracking-wide text-gray-200">{hoveredSummary.label}</span>
                                            <span className={`font-mono font-semibold ${hoveredSummary.tone === 'negative' ? 'text-red-200' : 'text-green-200'}`}>
                                                {formatValue(hoveredSummary.value)}
                                            </span>
                                        </div>
                                    ) : null}
                                </div>
                            ) : null}
                        </div>
                    </div>

                    {/* Legend — series entries toggle their series; the reference and its gap are keys, not filters. */}
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 border-t border-slate-100 px-4 py-2.5">
                        {series.map((entry) => {
                            const on = isEnabled(entry.id);
                            return (
                                <button
                                    key={entry.id}
                                    type="button"
                                    onClick={() => toggleSeries(entry.id)}
                                    aria-pressed={on}
                                    data-testid={`${chartId}-legend-${entry.id}`}
                                    title={on ? `Hide ${entry.label} from the chart` : `Show ${entry.label} on the chart`}
                                    className={`inline-flex items-center gap-1.5 rounded-md px-1.5 py-0.5 text-xs transition-colors hover:bg-slate-100 ${on ? 'text-slate-700' : 'text-slate-400'}`}
                                >
                                    <span
                                        className="inline-block h-2.5 w-2.5 rounded-sm"
                                        style={{ backgroundColor: on ? entry.color : SURFACE, border: on ? 'none' : `1px solid ${entry.color}` }}
                                        aria-hidden
                                    />
                                    <span className={on ? '' : 'line-through'}>{entry.label}</span>
                                </button>
                            );
                        })}
                        {reference?.gap ? (
                            <span className="inline-flex items-center gap-1.5 px-1.5 py-0.5 text-xs text-slate-700">
                                <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: reference.gap.color }} aria-hidden />
                                {reference.gap.label}
                            </span>
                        ) : null}
                        {reference ? (
                            <span className="inline-flex items-center gap-1.5 px-1.5 py-0.5 text-xs text-slate-700">
                                <svg width="14" height="4" aria-hidden className="shrink-0">
                                    <line x1="0" y1="2" x2="14" y2="2" stroke={INK_PRIMARY} strokeWidth="2" strokeDasharray="1 4" strokeLinecap="round" />
                                </svg>
                                {reference.label}
                            </span>
                        ) : null}
                    </div>
                </>
            )}
        </div>
    );
};

export default ProjectionChart;
